import { type PreferredProductData } from "../../../models/PdpPageProductData";

import { useBooleanState } from "../../../../common/hooks/useBooleanState";
import { useTranslation } from "react-i18next";
import { Button, Grid, Paper, Stack, Typography } from "@mui/material";
import { PreferredProductsItem } from "./PreferredProductsItem";
import { PackageRestrictionContext } from "~/common/components/PackageRestrictionContext/PackageRestrictionContext";
import { PREFERRED_PRODUCTS_ID } from "~/ProductDetails/constants";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { C_SURFACE_BRIGHT } from "~/common/components/icon/Colors";
import { userIsCustomer } from "~/utils/roles";

const MAX_VISIBLE_PRODUCTS_DESKTOP = 8;
const MAX_VISIBLE_PRODUCTS_MOBILE = 4;

interface Props {
  productFamilyName: string;
  products: PreferredProductData[];

  showAddToCart: boolean;
}

export const PreferredProductsSection = (props: Props) => {
  const { t } = useTranslation();
  const { val: showAll, toggle: toggleShowAll } = useBooleanState(false);
  const isDesktop = useIsMinDesktopSize();
  const maxVisibleProducts = isDesktop ? MAX_VISIBLE_PRODUCTS_DESKTOP : MAX_VISIBLE_PRODUCTS_MOBILE;
  const showMore = props.products.length > maxVisibleProducts;

  const visibleProducts = showAll ? props.products : props.products.slice(0, maxVisibleProducts);
  const isBusinessUser = userIsCustomer();

  if (props.products.length === 0) {
    return null;
  }

  return (
    <PackageRestrictionContext value={""}>
      <Paper sx={{ p: 2, background: C_SURFACE_BRIGHT }} id={PREFERRED_PRODUCTS_ID}>
        <Stack gap={3}>
          <Stack gap={1}>
            <Typography variant="titleLarge">
              {t("pdp.preferredProducts.title", { code: props.productFamilyName })}
            </Typography>
            {isBusinessUser && <Typography variant="bodyLarge">{t("pdp.preferredProducts.description")}</Typography>}
          </Stack>
          <Grid container columns={{ phone: 1, tablet: 2, "desktop-s": 4 }} spacing={2}>
            {visibleProducts.map((product) => (
              <Grid size={1} key={product.code}>
                <PreferredProductsItem product={product} showAddToCart={props.showAddToCart} />
              </Grid>
            ))}
          </Grid>
          {showMore && (
            <Button variant="text" sx={{ alignSelf: "flex-start" }} onClick={toggleShowAll}>
              {t(`pdp.preferredProducts.button.${showAll ? "hideAll" : "showAll"}`)}
            </Button>
          )}
        </Stack>
      </Paper>
    </PackageRestrictionContext>
  );
};
